import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { EnergyAnalysisRow, EnergyAnalysisSplitDimension } from "../model/types";

import {
  buildDimensionRankedBarOption,
  buildDimensionTreemapOption,
} from "../lib/buildDimensionInsightOptions";
import { buildTrendSeries, collectYears } from "../lib/aggregate";
import { buildTrendChartOption } from "../lib/buildTrendChartOption";
import { ENERGY_ANALYSIS_TREND_CHART_TYPE } from "../model/constants";
import { EnergyAnalysisInsightChart } from "./EnergyAnalysisInsightChart";

type EnergyAnalysisDimensionInsightsProps = {
  dimension: EnergyAnalysisSplitDimension;
  rows: EnergyAnalysisRow[];
  snapshotYear?: number;
  unit: string;
};

export const EnergyAnalysisDimensionInsights = ({
  dimension,
  rows,
  snapshotYear,
  unit,
}: EnergyAnalysisDimensionInsightsProps) => {
  const { t } = useTranslation();
  const years = useMemo(() => collectYears(rows), [rows]);
  const year = snapshotYear ?? years[years.length - 1];
  const label = t(`BuildingProfile.energyAnalysis.tabs.${dimension}`);
  const rankedBar = useMemo(
    () => buildDimensionRankedBarOption(rows, dimension, unit, snapshotYear),
    [dimension, rows, snapshotYear, unit]
  );
  const treemap = useMemo(
    () => buildDimensionTreemapOption(rows, dimension, unit, snapshotYear),
    [dimension, rows, snapshotYear, unit]
  );
  const percentTrend = useMemo(() => {
    const trend = buildTrendSeries(rows, dimension);
    return buildTrendChartOption(
      trend.years,
      trend.series,
      ENERGY_ANALYSIS_TREND_CHART_TYPE.stackedPercent
    );
  }, [dimension, rows]);
  return (
    <>
      <EnergyAnalysisInsightChart
        height="28rem"
        iconName="Barchart"
        option={rankedBar}
        title={t("BuildingProfile.energyAnalysis.dimensionInsights.ranked", { dimension: label, year })}
      />
      <EnergyAnalysisInsightChart
        height="24rem"
        iconName="GridIcon"
        option={treemap}
        title={t("BuildingProfile.energyAnalysis.dimensionInsights.share", { dimension: label, year })}
      />
      <EnergyAnalysisInsightChart
        height="28rem"
        iconName="Barchart"
        option={percentTrend}
        title={t("BuildingProfile.energyAnalysis.dimensionInsights.percentTrend", {
          dimension: label,
        })}
      />
    </>
  );
};
